import React from 'react';
import { AddOn, Appointment, BookingAvailabilitySlot, Service, Venue } from '@/types';
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { getAvailableTimeSlotsForDate, isDateBlockedBySchedule } from '@/lib/scheduling';

interface BookingData {
  venueId: string;
  serviceIds: string[];
  addOnIds: string[];
  date: Date | null;
  time: string;
}

interface BookingScheduleStepProps {
  venues: Venue[];
  services: Service[];
  addOns: AddOn[];
  availableAddOns: AddOn[];
  bookingData: BookingData;
  onBookingDataChange: (data: BookingData) => void;
  getPrice: (item: Service | AddOn) => number;
  requiresAddOnSelection: boolean;
  isAddOnSelectionMissing: boolean;
  selectedVenue: Venue | undefined;
  visibleMonth: Date;
  onMonthChange: (offset: number) => void;
  canGoPrevMonth: boolean;
  availabilitySlots: BookingAvailabilitySlot[];
  hasAvailabilityData: boolean;
  isLoadingAvailability: boolean;
  allowOverbook: boolean;
  onAllowOverbookChange: (value: boolean) => void;
  requiredDuration: number;
  appointments: Appointment[];
  servicesById: Map<string, Service>;
  addOnsById: Map<string, AddOn>;
}

export const BookingScheduleStep: React.FC<BookingScheduleStepProps> = ({
  venues,
  services,
  availableAddOns,
  bookingData,
  onBookingDataChange,
  getPrice,
  requiresAddOnSelection,
  isAddOnSelectionMissing,
  selectedVenue,
  visibleMonth,
  onMonthChange,
  canGoPrevMonth,
  availabilitySlots,
  hasAvailabilityData,
  isLoadingAvailability,
  allowOverbook,
  onAllowOverbookChange,
  requiredDuration,
  appointments,
  servicesById,
  addOnsById
}) => {
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const minBookableDate = new Date(today);
  const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const dates = Array.from(
    { length: monthEnd.getDate() },
    (_, i) => new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), i + 1)
  );
  const leadingEmptyDays = monthStart.getDay();
  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const availabilityByTime = new Map(availabilitySlots.map((slot) => [slot.time, slot]));
  const generatedSlots = bookingData.date
    ? getAvailableTimeSlotsForDate(bookingData.date, selectedVenue?.schedule, {
        requiredDurationMinutes: requiredDuration
      })
    : [];
  // This CREATE flow renders ALL slots as a button grid and gates each button
  // with `disabled={!allowOverbook && isConflict}`, so it intentionally does NOT
  // filter here. The edit/company dropdowns share slotTimesForSelection in
  // @/lib/bookingSlots instead — keep the two staff overbook flows in sync.
  const availableSlots = bookingData.date
    ? hasAvailabilityData
      ? availabilitySlots.map((s) => s.time)
      : generatedSlots
    : [];

  const getMinutes = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const fallbackBusyIntervals = !hasAvailabilityData
    ? appointments
        .filter(
          (app) =>
            app.venueId === bookingData.venueId &&
            new Date(app.date).toDateString() === bookingData.date?.toDateString() &&
            app.status !== 'cancelled'
        )
        .map((app) => {
          const appServiceIds =
            Array.isArray(app.serviceIds) && app.serviceIds.length > 0
              ? app.serviceIds
              : [app.serviceId];
          let appDuration = appServiceIds.reduce(
            (sum, sid) => sum + (servicesById.get(sid)?.durationMinutes || 0),
            0
          );
          if (appDuration <= 0) appDuration = 30;
          app.addOnIds.forEach((aid) => {
            const addon = addOnsById.get(aid);
            if (addon) appDuration += addon.durationMinutes || 0;
          });
          const start = getMinutes(app.time);
          return { start, end: start + appDuration };
        })
    : [];

  const toggleAddOn = (id: string) => {
    onBookingDataChange({
      ...bookingData,
      addOnIds: bookingData.addOnIds.includes(id)
        ? bookingData.addOnIds.filter((aid) => aid !== id)
        : [...bookingData.addOnIds, id]
    });
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Selection Panel */}
      <div className="lg:w-1/3 space-y-6">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
            Location / Venue
          </label>
          <select
            className="w-full border rounded-lg p-2.5 text-sm bg-white"
            value={bookingData.venueId}
            onChange={(e) =>
              onBookingDataChange({ ...bookingData, venueId: e.target.value, date: null, time: '' })
            }
          >
            <option value="">Select Venue</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
            Services (select one or more)
          </label>
          <div className="border rounded-lg bg-white p-2 space-y-1 max-h-48 overflow-y-auto">
            {services.map((s) => (
              <label
                key={s.id}
                className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${bookingData.serviceIds.includes(s.id) ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-teal-600 rounded"
                    checked={bookingData.serviceIds.includes(s.id)}
                    onChange={(e) =>
                      onBookingDataChange({
                        ...bookingData,
                        serviceIds: e.target.checked
                          ? [...bookingData.serviceIds, s.id]
                          : bookingData.serviceIds.filter((id) => id !== s.id),
                        time: ''
                      })
                    }
                  />
                  <span className="text-sm font-medium text-slate-700">{s.name}</span>
                </div>
                <span className="text-xs text-slate-500">£{getPrice(s).toFixed(2)}</span>
              </label>
            ))}
          </div>
        </div>
        {bookingData.serviceIds.length > 0 && (
          <div className="pt-4 border-t">
            <label className="block text-sm font-bold text-slate-700 mb-3">
              {requiresAddOnSelection ? 'Add-ons (required)' : 'Optional Add-ons'}
            </label>
            <div className="space-y-2">
              {availableAddOns.map((addon) => (
                <label
                  key={addon.id}
                  className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-colors ${bookingData.addOnIds.includes(addon.id) ? 'bg-teal-50 border-teal-400' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-teal-600 rounded"
                      checked={bookingData.addOnIds.includes(addon.id)}
                      onChange={() => toggleAddOn(addon.id)}
                    />
                    <span className="text-sm font-medium text-slate-700">{addon.name}</span>
                  </div>
                  <span className="text-xs text-slate-500">+£{getPrice(addon).toFixed(2)}</span>
                </label>
              ))}
              {isAddOnSelectionMissing && (
                <p className="text-xs font-semibold text-amber-600">
                  Select at least one add-on to continue with Health Surveillance.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Calendar Panel */}
      <div className="flex-1 bg-slate-50 p-6 rounded-2xl border border-slate-200">
        {!bookingData.venueId ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-center">
            <Calendar className="w-12 h-12 mb-2 opacity-20" />
            <p>Please select a location first to view availability.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h4 className="font-bold text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Select Date
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onMonthChange(-1)}
                    disabled={!canGoPrevMonth}
                    className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="text-sm font-bold text-slate-800 min-w-[140px] text-center">
                    {visibleMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                  </div>
                  <button
                    onClick={() => onMonthChange(1)}
                    className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 flex items-center justify-center"
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {weekdayLabels.map((label) => (
                  <div
                    key={label}
                    className="text-[10px] font-bold text-slate-400 text-center uppercase"
                  >
                    {label}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: leadingEmptyDays }).map((_, idx) => (
                  <div key={`empty-${idx}`} />
                ))}
                {dates.map((date) => {
                  const isSelected = bookingData.date?.toDateString() === date.toDateString();
                  const { blocked, reason } = isDateBlockedBySchedule(
                    date,
                    selectedVenue?.schedule
                  );
                  const beforeBookingWindow = date.getTime() < minBookableDate.getTime();
                  const isToday = date.toDateString() === now.toDateString();
                  const hasSlotsToday =
                    isToday && selectedVenue
                      ? getAvailableTimeSlotsForDate(date, selectedVenue.schedule, {
                          requiredDurationMinutes: requiredDuration
                        }).length > 0
                      : true;
                  const noSlotsLeftToday = !blocked && isToday && !hasSlotsToday;
                  const isUnavailable = blocked || beforeBookingWindow || noSlotsLeftToday;
                  return (
                    <button
                      key={date.toISOString()}
                      disabled={isUnavailable}
                      title={
                        beforeBookingWindow
                          ? 'Bookings cannot be in the past.'
                          : blocked
                            ? reason
                            : noSlotsLeftToday
                              ? 'No remaining slots today.'
                              : reason
                      }
                      onClick={() => onBookingDataChange({ ...bookingData, date, time: '' })}
                      className={`py-3 px-1 rounded-lg border text-center transition-all ${isSelected ? 'bg-teal-600 text-white border-teal-600' : 'bg-white hover:border-teal-400'} ${isUnavailable ? 'opacity-40 cursor-not-allowed bg-slate-100' : ''}`}
                    >
                      <div className="text-[10px] font-bold uppercase opacity-75">
                        {date.getDate()}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {bookingData.date && (
              <div className="animate-fade-in">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-slate-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Select Available Time Slot
                  </h4>
                  <label className="flex items-center gap-2 text-[11px] font-bold text-slate-600">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      checked={allowOverbook}
                      onChange={(e) => onAllowOverbookChange(e.target.checked)}
                    />
                    Allow overbook
                  </label>
                </div>
                {allowOverbook && (
                  <p className="text-[11px] text-red-500 mb-3">
                    Overbooking enabled: fully booked slots can be selected.
                  </p>
                )}
                {isLoadingAvailability ? (
                  <p className="text-sm text-slate-500 italic">Loading availability...</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">
                    No opening hours defined for this day.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {availableSlots.map((time) => {
                      const availabilitySlot = availabilityByTime.get(time);
                      const slotStart = getMinutes(time);
                      const slotEnd = slotStart + requiredDuration;
                      const fallbackConflict = fallbackBusyIntervals.some(
                        (interval) => slotStart < interval.end && slotEnd > interval.start
                      );
                      const isConflict = availabilitySlot
                        ? !availabilitySlot.available
                        : fallbackConflict;
                      return (
                        <button
                          key={time}
                          disabled={!allowOverbook && isConflict}
                          title={isConflict ? 'Slot is fully booked' : undefined}
                          onClick={() => onBookingDataChange({ ...bookingData, time })}
                          className={`py-2 px-3 text-xs rounded-lg border transition-all font-medium ${bookingData.time === time ? 'bg-teal-600 text-white border-teal-600' : isConflict ? (allowOverbook ? 'bg-red-50 text-red-600 border-red-200 hover:border-red-300' : 'bg-red-50 text-red-300 border-red-100 cursor-not-allowed line-through') : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400'}`}
                        >
                          {time}
                          {availabilitySlot && availabilitySlot.capacity > 1
                            ? ` (${availabilitySlot.remaining} left)`
                            : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
                {bookingData.time && (
                  <div className="mt-6 p-4 bg-teal-50 border border-teal-100 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="text-xs text-teal-600 font-bold uppercase">Selected Slot</p>
                      <p className="text-teal-900 font-bold">
                        {bookingData.date.toLocaleDateString('en-GB', { dateStyle: 'full' })} at{' '}
                        {bookingData.time}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-teal-600 font-bold uppercase">Total Duration</p>
                      <p className="text-teal-900 font-bold">{requiredDuration} mins</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
